import React, { useState, useEffect } from 'react';
import { useStore, notifyWhatsApp } from '../context/ProductStore';
import MediaUpload from './MediaUpload';
import QuotationModal from './QuotationModal';

const STATUS_COLORS = {
  pending:   { bg: 'rgba(234,179,8,0.15)',  border: 'rgba(234,179,8,0.35)',  text: '#fbbf24', label: '⏳ Pending'   },
  paid:      { bg: 'rgba(22,163,74,0.15)',  border: 'rgba(22,163,74,0.35)',  text: '#4ade80', label: '✅ Paid'      },
  cancelled: { bg: 'rgba(220,38,38,0.15)',  border: 'rgba(220,38,38,0.35)',  text: '#f87171', label: '❌ Cancelled' },
};

const PAYMENT_LABELS = {
  ecocash:  '📱 EcoCash',
  swipe:    '💳 Swipe',
  transfer: '🏦 Transfer',
};

const BRANDS_BY_CATEGORY = {
  Laptops:       ['HP', 'Dell', 'Lenovo', 'Asus', 'Acer', 'Samsung', 'Apple', 'MSI', 'Toshiba', 'Other'],
  Phones:        ['Samsung', 'iPhone', 'Huawei', 'Tecno', 'Infinix', 'Nokia', 'Oppo', 'Xiaomi', 'Other'],
  Desktops:      ['HP', 'Dell', 'Lenovo', 'Asus', 'Acer', 'Custom Build', 'Other'],
  Printers:      ['HP', 'Epson', 'Canon', 'Brother', 'Samsung', 'Ricoh', 'Other'],
  Networking:    ['Starlink', 'TP-Link', 'Huawei', 'Mikrotik', 'Cisco', 'Ubiquiti', 'D-Link', 'Other'],
  'PA Systems':  ['JBL', 'Yamaha', 'Sony', 'Bose', 'Behringer', 'Mackie', 'QSC', 'Other'],
  Accessories:   ['Logitech', 'HP', 'Dell', 'Samsung', 'Havit', 'Redragon', 'Generic', 'Other'],
  'Laptop Bags': ['Targus', 'HP', 'Dell', 'Samsonite', 'Swiss Gear', 'Generic', 'Other'],
};

const MAX_VIDEO_SECONDS = 15;

const compressImage = (file, maxW = 800, quality = 0.65) =>
  new Promise((resolve) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const scale = Math.min(1, maxW / img.width);
      const canvas = document.createElement('canvas');
      canvas.width  = img.width  * scale;
      canvas.height = img.height * scale;
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL('image/jpeg', quality));
      URL.revokeObjectURL(url);
    };
    img.src = url;
  });

const trimVideo = (file) =>
  new Promise((resolve, reject) => {
    const video = document.createElement('video');
    const url   = URL.createObjectURL(file);
    video.src   = url;
    video.muted = true;
    video.onloadedmetadata = () => {
      const duration = video.duration;
      if (duration <= MAX_VIDEO_SECONDS + 0.5) {
        const reader = new FileReader();
        reader.onload = (e) => { URL.revokeObjectURL(url); resolve({ b64: e.target.result, trimmed: false, duration }); };
        reader.onerror = reject;
        reader.readAsDataURL(file);
        return;
      }
      const canvas  = document.createElement('canvas');
      const ctx     = canvas.getContext('2d');
      canvas.width  = video.videoWidth  || 640;
      canvas.height = video.videoHeight || 360;
      const stream   = canvas.captureStream(24);
      const recorder = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp8' });
      const chunks   = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data); };
      recorder.onstop = () => {
        const blob   = new Blob(chunks, { type: 'video/webm' });
        const reader = new FileReader();
        reader.onload = (e) => { URL.revokeObjectURL(url); resolve({ b64: e.target.result, trimmed: true, duration }); };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      };
      const drawFrame = () => { ctx.drawImage(video, 0, 0, canvas.width, canvas.height); };
      const interval  = setInterval(drawFrame, 1000 / 24);
      video.play();
      recorder.start();
      setTimeout(() => { recorder.stop(); clearInterval(interval); video.pause(); }, MAX_VIDEO_SECONDS * 1000);
    };
    video.onerror = () => reject(new Error('Could not load video'));
  });

const AdminPanel = ({ onClose }) => {
  const {
    products, addProduct, removeProduct, toggleOutOfStock, updateProduct,
    CATEGORIES, inStockProducts, outOfStockProducts, promoProducts, categoryStats,
    orders, pendingOrders, paidOrders, cancelledOrders, totalRevenue,
    updateOrderStatus, deleteOrder, changeAdminPin,
    FOR_TAGS, forTagBanners, setForTagItems, addForTagItem, updateForTagItem, removeForTagItem,
    quoteRequests, newQuoteRequests, updateQuoteRequestStatus, deleteQuoteRequest,
  } = useStore();

  const [tab,     setTab]    = useState('orders');
  const [filter,  setFilter] = useState('all');
  const [oFilter, setOFilter]= useState('all');
  const [qFilter, setQFilter]= useState('all');
  const [success, setSuccess]= useState('');
  const [editId,  setEditId] = useState(null);
  const [quotingFor, setQuotingFor] = useState(null); // customer data to prefill formal quote builder

  const [forTagUploading, setForTagUploading] = useState(null);
  const [forTagMsg, setForTagMsg] = useState('');

  // ── Add a new empty product entry for a tag ──
  const handleAddForTagProduct = async (tag) => {
    const current = forTagBanners?.[tag] || [];
    const newEntry = { images: [], title: '', description: '' };
    await setForTagItems(tag, [...current, newEntry]);
  };

  // ── Add images to an existing product entry — aggressive compression ──
  const handleAddImagesToEntry = async (tag, entryIdx, files) => {
    const imageFiles = Array.from(files).filter(f => f.type.startsWith('image/'));
    if (!imageFiles.length) return;
    setForTagUploading(`${tag}-${entryIdx}`);
    try {
      // Compress hard: max 400px wide, 0.4 quality — keeps each image ~15-30KB
      const compressed = await Promise.all(imageFiles.map(f => compressImage(f, 400, 0.4)));
      const current = forTagBanners?.[tag] || [];
      const next = current.map((entry, i) => {
        if (i !== entryIdx) return entry;
        const existingImages = entry.images || (entry.image ? [entry.image] : []);
        return { ...entry, images: [...existingImages, ...compressed] };
      });
      // Check estimated size before saving
      const json = JSON.stringify(next);
      const sizeKB = Math.round(new TextEncoder().encode(json).length / 1024);
      if (sizeKB > 900) {
        alert(`Images too large (${sizeKB}KB total). Please use fewer images or smaller files per product.`);
        return;
      }
      await setForTagItems(tag, next);
      setForTagMsg(`✅ ${imageFiles.length} photo${imageFiles.length > 1 ? 's' : ''} added!`);
      setTimeout(() => setForTagMsg(''), 2000);
    } catch (err) {
      alert('Upload error: ' + err.message);
    } finally {
      setForTagUploading(null);
    }
  };

  // ── Remove one image from an entry ──
  const handleRemoveEntryImage = async (tag, entryIdx, imgIdx) => {
    const current = forTagBanners?.[tag] || [];
    const next = current.map((entry, i) => {
      if (i !== entryIdx) return entry;
      const imgs = entry.images || (entry.image ? [entry.image] : []);
      return { ...entry, images: imgs.filter((_, ii) => ii !== imgIdx) };
    });
    await setForTagItems(tag, next);
  };

  const handleRemoveForTagItem = async (tag, index) => {
    if (!window.confirm(`Remove this product entry from "${tag}"?`)) return;
    await removeForTagItem(tag, index);
  };

  const handleForTagTextChange = (tag, index, field, value) => {
    const current = forTagBanners?.[tag] || [];
    const next = current.map((item, i) => i === index ? { ...item, [field]: value } : item);
    setForTagItems(tag, next);
  };

  const [currentPin,  setCurrentPin]  = useState('');
  const [newPin,      setNewPin]      = useState('');
  const [confirmPin,  setConfirmPin]  = useState('');
  const [pinLoading,  setPinLoading]  = useState(false);
  const [pinMsg,      setPinMsg]      = useState(null);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew,     setShowNew]     = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleChangePin = async () => {
    setPinMsg(null);
    if (!currentPin) return setPinMsg({ type: 'error', text: 'Enter your current PIN.' });
    if (!newPin)     return setPinMsg({ type: 'error', text: 'Enter a new PIN.' });
    if (newPin !== confirmPin) return setPinMsg({ type: 'error', text: 'New PINs do not match.' });
    if (newPin.length < 6) return setPinMsg({ type: 'error', text: 'New PIN must be at least 6 characters.' });
    setPinLoading(true);
    const result = await changeAdminPin(currentPin, newPin);
    setPinLoading(false);
    if (result.success) {
      setPinMsg({ type: 'success', text: '✅ PIN changed successfully! Use your new PIN next time you log in.' });
      setCurrentPin(''); setNewPin(''); setConfirmPin('');
    } else {
      setPinMsg({ type: 'error', text: result.error });
    }
  };

  const emptyForm = () => ({
    name: '', category: CATEGORIES[0], price: '', brand: '',
    description: '', isPromo: false, promoPrice: '',
    images: [], video: null, videoInfo: null,
    specs: [{ key: '', value: '' }],
    forTags: [],
  });
  const [form, setForm] = useState(emptyForm());

  useEffect(() => {
    setForm(f => ({ ...f, brand: '' }));
  }, [form.category]);

  const addSpec    = () => setForm(f => ({ ...f, specs: [...f.specs, { key: '', value: '' }] }));
  const removeSpec = (i) => setForm(f => ({ ...f, specs: f.specs.filter((_, idx) => idx !== i) }));
  const updateSpec = (i, field, val) =>
    setForm(f => ({ ...f, specs: f.specs.map((s, idx) => idx === i ? { ...s, [field]: val } : s) }));

  const toggleForTag = (tag) =>
    setForm(f => ({
      ...f,
      forTags: f.forTags.includes(tag)
        ? f.forTags.filter(t => t !== tag)
        : [...f.forTags, tag],
    }));

  const handleSubmit = async () => {
    if (!form.name.trim() || !form.price) return alert('Name and price are required.');
    if (form.isPromo && !form.promoPrice)  return alert('Enter a promo price.');
    if (form.images.length === 0) return alert('Please add at least 1 product photo.');

    const data = {
      name:        form.name.trim(),
      category:    form.category,
      brand:       (form.brand === '_typing_' ? '' : form.brand).trim(),
      price:       parseFloat(form.price),
      description: form.description.trim(),
      isPromo:     form.isPromo,
      promoPrice:  form.isPromo ? parseFloat(form.promoPrice) : null,
      image:       form.images[0] || null,
      images:      form.images,
      video:       form.video || null,
      videoInfo:   form.videoInfo || null,
      hasMedia:    true,
      specs:       form.specs.filter(s => s.key.trim() && s.value.trim()),
      forTags:     form.forTags,
    };

    if (editId) {
      await updateProduct(editId, { ...data, mediaKey: editId });
      setSuccess('Product updated! ✅');
      setEditId(null);
    } else {
      try {
        const saved = await addProduct(data);
        const realId = saved?.id;
        if (realId) {
          await updateProduct(realId, { mediaKey: realId });
          setSuccess('Product added! ✅');
        } else {
          setSuccess('Product saved ✅');
        }
      } catch (err) {
        alert('Error saving product: ' + err.message);
        return;
      }
    }

    setForm(emptyForm());
    setTimeout(() => setSuccess(''), 3000);
  };

  const startEdit = (p) => {
    setEditId(p.id);
    setForm({
      name: p.name, category: p.category, brand: p.brand || '',
      price: p.price.toString(), description: p.description || '',
      isPromo: p.isPromo || false, promoPrice: p.promoPrice ? p.promoPrice.toString() : '',
      images: p.images || (p.image ? [p.image] : []),
      video: p.video || null, videoInfo: p.videoInfo || null,
      specs: (p.specs && p.specs.length) ? p.specs : [{ key: '', value: '' }],
      forTags: p.forTags || [],
    });
    setTab('add');
  };

  const filteredProducts = products.filter(p =>
    filter === 'instock' ? !p.outOfStock : filter === 'outofstock' ? p.outOfStock : filter === 'promo' ? p.isPromo : true
  );
  const filteredOrders = orders.filter(o =>
    oFilter === 'pending' ? o.status === 'pending' : oFilter === 'paid' ? o.status === 'paid' : oFilter === 'cancelled' ? o.status === 'cancelled' : true
  );
  const filteredQuoteRequests = quoteRequests.filter(q =>
    qFilter === 'new' ? q.status === 'new' : qFilter === 'contacted' ? q.status === 'contacted' : qFilter === 'closed' ? q.status === 'closed' : true
  );
  const currentBrands = BRANDS_BY_CATEGORY[form.category] || [];
  const tabs = [
    ['orders', `📋 Orders${pendingOrders.length > 0 ? ` (${pendingOrders.length})` : ''}`],
    ['quotes', `📄 Quote Requests${newQuoteRequests.length > 0 ? ` (${newQuoteRequests.length})` : ''}`],
    ['add',    editId ? '✏️ Edit Product' : '➕ Add Product'],
    ['manage', '📦 Manage Stock'],
    ['fortags','🏷️ For Tags'],
    ['stats',  '📊 Statistics'],
    ['security','🔐 Security'],
  ];

  return (
    <div style={S.overlay}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=League+Spartan:wght@400;500;600;700;800;900&display=swap');
        .admin-panel * { font-family: 'League Spartan', sans-serif !important; }
        .admin-input:focus { border-color: #CC0000 !important; outline: none; }
        .pin-input-wrap { position: relative; display: flex; align-items: center; }
        .pin-input-wrap input { padding-right: 44px !important; }
        .pin-eye { position: absolute; right: 12px; background: none; border: none; cursor: pointer; color: rgba(255,255,255,0.4); font-size: 18px; padding: 0; line-height: 1; }
        .pin-eye:hover { color: rgba(255,255,255,0.8); }
      `}</style>
      <div style={S.panel} className="admin-panel">

        <div style={S.header}>
          <div>
            <h2 style={S.title}>🛠️ Admin Panel</h2>
            <p style={S.subtitle}>Techdave Solutions Management</p>
          </div>
          <button onClick={onClose} style={S.closeBtn}>✕</button>
        </div>

        <div style={S.tabs}>
          {tabs.map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} style={S.tab(tab === id)}>
              {label}
              {id === 'orders' && pendingOrders.length > 0 && <span style={S.tabAlert}>{pendingOrders.length}</span>}
              {id === 'quotes' && newQuoteRequests.length > 0 && <span style={S.tabAlert}>{newQuoteRequests.length}</span>}
            </button>
          ))}
        </div>

        {/* ══ ORDERS ══ */}
        {tab === 'orders' && (
          <div style={S.area}>
            <div style={S.revenueRow}>
              {[
                { label: 'Pending',   value: pendingOrders.length,          color: '#fbbf24', icon: '⏳' },
                { label: 'Paid',      value: paidOrders.length,             color: '#4ade80', icon: '✅' },
                { label: 'Cancelled', value: cancelledOrders.length,        color: '#f87171', icon: '❌' },
                { label: 'Revenue',   value: `$${totalRevenue.toFixed(0)}`, color: '#CC0000', icon: '💰' },
              ].map(s => (
                <div key={s.label} style={S.revCard(s.color)}>
                  <span style={S.revIcon}>{s.icon}</span>
                  <span style={S.revValue(s.color)}>{s.value}</span>
                  <span style={S.revLabel}>{s.label}</span>
                </div>
              ))}
            </div>
            <div style={S.filterRow}>
              {[['all','All'],['pending','⏳ Pending'],['paid','✅ Paid'],['cancelled','❌ Cancelled']].map(([v,l]) => (
                <button key={v} onClick={() => setOFilter(v)} style={S.filterBtn(oFilter === v)}>{l}</button>
              ))}
            </div>
            <div style={S.orderList}>
              {filteredOrders.length === 0 && <div style={S.empty}>{orders.length === 0 ? '📋 No orders yet.' : 'No orders match this filter.'}</div>}
              {filteredOrders.map(order => {
                const sc = STATUS_COLORS[order.status];
                return (
                  <div key={order.id} style={S.orderCard(sc)}>
                    <div style={S.orderHeader}>
                      <div>
                        <div style={S.orderId}>#{order.id}</div>
                        <div style={S.orderDate}>{new Date(order.createdAt).toLocaleString()}</div>
                      </div>
                      <span style={S.statusBadge(sc)}>{sc.label}</span>
                    </div>
                    <div style={S.orderCustomer}>
                      {[['👤',order.customerName,true],['📞',order.customerPhone,false],order.address&&['📍',order.address,false],['💳',PAYMENT_LABELS[order.paymentMethod]||order.paymentMethod,false]].filter(Boolean).map(([icon,val,bold],i)=>(
                        <div key={i} style={S.customerRow}><span style={S.customerIcon}>{icon}</span><span style={bold?S.customerName:{}}>{val}</span></div>
                      ))}
                    </div>
                    <div style={S.orderItems}>
                      {order.items.map((item,idx)=>(
                        <div key={idx} style={S.orderItem}><span>{item.name} × {item.qty}</span><span>USD {(item.unitPrice*item.qty).toFixed(2)}</span></div>
                      ))}
                      <div style={S.orderTotal}><span>TOTAL</span><span style={{color:'#CC0000'}}>USD {order.total.toFixed(2)}</span></div>
                    </div>
                    <div style={S.orderActions}>
                      <div style={S.statusBtns}>
                        {order.status!=='paid'      && <button onClick={()=>updateOrderStatus(order.id,'paid')}      style={S.actionBtn('#16a34a')}>✅ Mark Paid</button>}
                        {order.status!=='pending'   && <button onClick={()=>updateOrderStatus(order.id,'pending')}   style={S.actionBtn('#d97706')}>⏳ Mark Pending</button>}
                        {order.status!=='cancelled' && <button onClick={()=>{if(window.confirm('Cancel?'))updateOrderStatus(order.id,'cancelled');}} style={S.actionBtn('#dc2626')}>❌ Cancel</button>}
                      </div>
                      <div style={S.waBtns}>
                        <button onClick={()=>notifyWhatsApp(order,0)} style={S.waBtn}>📱 0772 758 875</button>
                        <button onClick={()=>notifyWhatsApp(order,1)} style={S.waBtn}>📱 0785 661 758</button>
                        <button onClick={()=>{if(window.confirm('Delete permanently?'))deleteOrder(order.id);}} style={S.deleteBtn}>🗑️</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ══ QUOTE REQUESTS (customer inquiries) ══ */}
        {tab === 'quotes' && (
          <div style={S.area}>
            <div style={S.revenueRow}>
              {[
                { label: 'New',       value: quoteRequests.filter(q=>q.status==='new').length,       color: '#fbbf24', icon: '🆕' },
                { label: 'Contacted', value: quoteRequests.filter(q=>q.status==='contacted').length, color: '#60a5fa', icon: '📞' },
                { label: 'Closed',    value: quoteRequests.filter(q=>q.status==='closed').length,    color: '#4ade80', icon: '✅' },
                { label: 'Total',     value: quoteRequests.length,                                   color: '#CC0000', icon: '📄' },
              ].map(s => (
                <div key={s.label} style={S.revCard(s.color)}>
                  <span style={S.revIcon}>{s.icon}</span>
                  <span style={S.revValue(s.color)}>{s.value}</span>
                  <span style={S.revLabel}>{s.label}</span>
                </div>
              ))}
            </div>
            <p style={S.hint}>
              These are inquiries submitted by customers via "Request a Quotation" on the Shop page.
              Review what they need, then reply directly or build a formal priced quote.
            </p>
            <div style={S.filterRow}>
              {[['all','All'],['new','🆕 New'],['contacted','📞 Contacted'],['closed','✅ Closed']].map(([v,l]) => (
                <button key={v} onClick={() => setQFilter(v)} style={S.filterBtn(qFilter === v)}>{l}</button>
              ))}
            </div>
            <div style={S.orderList}>
              {filteredQuoteRequests.length === 0 && (
                <div style={S.empty}>{quoteRequests.length === 0 ? '📄 No quote requests yet.' : 'No requests match this filter.'}</div>
              )}
              {filteredQuoteRequests.map(req => {
                const qsc = req.status === 'new' ? { bg:'rgba(234,179,8,0.15)', border:'rgba(234,179,8,0.35)', text:'#fbbf24', label:'🆕 New' }
                          : req.status === 'contacted' ? { bg:'rgba(59,130,246,0.15)', border:'rgba(59,130,246,0.35)', text:'#60a5fa', label:'📞 Contacted' }
                          : { bg:'rgba(22,163,74,0.15)', border:'rgba(22,163,74,0.35)', text:'#4ade80', label:'✅ Closed' };
                const createdLabel = req.createdAt?.toDate ? req.createdAt.toDate().toLocaleString() : 'Just now';
                return (
                  <div key={req.id} style={S.orderCard(qsc)}>
                    <div style={S.orderHeader}>
                      <div>
                        <div style={S.orderId}>{req.name || 'Unnamed'}</div>
                        <div style={S.orderDate}>{createdLabel}</div>
                      </div>
                      <span style={S.statusBadge(qsc)}>{qsc.label}</span>
                    </div>
                    <div style={S.orderCustomer}>
                      {[['📞',req.phone,false],req.email&&['✉️',req.email,false],req.budget&&['💰',req.budget,false]].filter(Boolean).map(([icon,val,bold],i)=>(
                        <div key={i} style={S.customerRow}><span style={S.customerIcon}>{icon}</span><span style={bold?S.customerName:{}}>{val}</span></div>
                      ))}
                    </div>
                    <div style={S.orderItems}>
                      <div style={{ ...S.orderItem, flexDirection:'column', alignItems:'flex-start', gap:4 }}>
                        <span style={{ fontWeight:700, color:'rgba(255,255,255,0.5)', fontSize:11, textTransform:'uppercase', letterSpacing:'0.06em' }}>Looking for</span>
                        <span style={{ color:'#fff', fontSize:13.5, lineHeight:1.6 }}>{req.requirements}</span>
                      </div>
                    </div>
                    <div style={S.orderActions}>
                      <div style={S.statusBtns}>
                        {req.status!=='new'       && <button onClick={()=>updateQuoteRequestStatus(req.id,'new')}       style={S.actionBtn('#d97706')}>🆕 Mark New</button>}
                        {req.status!=='contacted' && <button onClick={()=>updateQuoteRequestStatus(req.id,'contacted')} style={S.actionBtn('#16a34a')}>📞 Mark Contacted</button>}
                        {req.status!=='closed'    && <button onClick={()=>updateQuoteRequestStatus(req.id,'closed')}    style={S.actionBtn('#16a34a')}>✅ Mark Closed</button>}
                      </div>
                      <div style={S.waBtns}>
                        <button
                          onClick={() => {
                            const digits = (req.phone || '').replace(/\D/g, '');
                            const phone = digits.startsWith('0') ? '263' + digits.slice(1) : digits;
                            const msg = `Hi ${req.name}, thanks for your quotation request with Techdave Solutions! Regarding: "${req.requirements}" — `;
                            window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, '_blank');
                          }}
                          style={S.waBtn}>
                          💬 Reply on WhatsApp
                        </button>
                        <button onClick={() => setQuotingFor({ name: req.name, phone: req.phone, email: req.email })} style={S.actionBtn('#d97706')}>
                          🧾 Build Formal Quote
                        </button>
                        <button onClick={()=>{if(window.confirm('Delete this request permanently?'))deleteQuoteRequest(req.id);}} style={S.deleteBtn}>🗑️</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ══ ADD / EDIT PRODUCT ══ */}
        {tab === 'add' && (
          <div style={S.area}>
            {success && <div style={S.successBanner}>✅ {success}</div>}
            {editId   && <div style={S.editBanner}>✏️ Editing product — make changes and save.</div>}

            <MediaUpload
              images={form.images}
              video={form.video}
              videoInfo={form.videoInfo}
              onImagesChange={(imgs) => setForm(f => ({ ...f, images: imgs }))}
              onVideoChange={(b64, info) => setForm(f => ({ ...f, video: b64, videoInfo: info }))}
              onVideoRemove={() => setForm(f => ({ ...f, video: null, videoInfo: null }))}
              compressImage={compressImage}
              trimVideo={trimVideo}
              MAX_VIDEO_SECONDS={MAX_VIDEO_SECONDS}
            />

            <div style={S.fields}>
              <div style={S.row2}>
                <div style={S.field}>
                  <label style={S.label}>Product Name *</label>
                  <input className="admin-input" style={S.input} value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    placeholder="e.g. HP Laptop 15s Core i7" />
                </div>
                <div style={S.field}>
                  <label style={S.label}>Category *</label>
                  <select className="admin-input" style={S.input} value={form.category}
                    onChange={e => setForm(f => ({ ...f, category: e.target.value, brand: '' }))}>
                    {CATEGORIES.map(c => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div style={S.row2}>
                <div style={S.field}>
                  <label style={S.label}>Brand</label>
                  <select className="admin-input" style={S.input}
                    value={currentBrands.includes(form.brand) ? form.brand : form.brand ? '__custom__' : ''}
                    onChange={e => {
                      if (e.target.value === '__custom__') {
                        setForm(f => ({ ...f, brand: '_typing_' }));
                      } else {
                        setForm(f => ({ ...f, brand: e.target.value }));
                      }
                    }}>
                    <option value="">-- Select Brand --</option>
                    {currentBrands.filter(b => b !== 'Other').map(b => <option key={b} value={b}>{b}</option>)}
                    <option value="__custom__">✏️ Other / Custom Brand...</option>
                  </select>
                  {/* Custom brand text input */}
                  {form.brand && !currentBrands.includes(form.brand) && (
                    <div style={{ marginTop: 8 }}>
                      <input className="admin-input"
                        style={{ ...S.input, borderColor: form.brand && form.brand !== '_typing_' ? 'rgba(74,222,128,0.6)' : 'rgba(204,0,0,0.5)' }}
                        placeholder="Type brand name e.g. Xiaomi, Realme, TCL, Hisense, Vivo..."
                        value={form.brand === '_typing_' ? '' : form.brand}
                        onChange={e => setForm(f => ({ ...f, brand: e.target.value || '_typing_' }))}
                        autoFocus
                      />
                      {form.brand && form.brand !== '_typing_' && (
                        <div style={{ fontSize: 11, color: '#4ade80', marginTop: 4, fontWeight: 600 }}>
                          ✓ Custom brand set: {form.brand}
                        </div>
                      )}
                    </div>
                  )}
                </div>
                <div style={S.field}>
                  <label style={S.label}>Price (USD) *</label>
                  <input className="admin-input" style={S.input} type="number" value={form.price}
                    onChange={e => setForm(f => ({ ...f, price: e.target.value }))} placeholder="0.00" />
                </div>
              </div>
              <div style={S.field}>
                <label style={S.label}>Promotion Deal</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                  <button onClick={() => setForm(f => ({ ...f, isPromo: !f.isPromo }))} style={S.toggleBtn(form.isPromo)}>
                    {form.isPromo ? '🔥 PROMO ON' : 'OFF'}
                  </button>
                  {form.isPromo && (
                    <input className="admin-input" style={{ ...S.input, flex: 1 }} type="number"
                      value={form.promoPrice}
                      onChange={e => setForm(f => ({ ...f, promoPrice: e.target.value }))}
                      placeholder="Promo price (USD)" />
                  )}
                </div>
              </div>
              <div style={S.field}>
                <label style={S.label}>Description</label>
                <textarea className="admin-input" style={{ ...S.input, minHeight: '80px', resize: 'vertical' }}
                  value={form.description}
                  onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                  placeholder="Product description, condition, details..." />
              </div>
              <div style={S.field}>
                <label style={S.label}>For</label>
                <p style={S.hint}>Tag this product so it appears under these "For" sections on the shop page.</p>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {FOR_TAGS.map(tag => (
                    <button key={tag} type="button" onClick={() => toggleForTag(tag)} style={S.forTagBtn(form.forTags.includes(tag))}>
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
                <div>
                  <label style={S.label}>Product Specifications</label>
                  <p style={S.hint}>Add specs like RAM, Storage, Color, Screen Size, Battery etc.</p>
                </div>
                <button onClick={addSpec} style={S.addSpecBtn}>+ Add Spec</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {form.specs.map((spec, i) => (
                  <div key={i} style={S.specRow}>
                    <input className="admin-input" style={{ ...S.input, flex: '0 0 38%' }}
                      value={spec.key} onChange={e => updateSpec(i, 'key', e.target.value)} placeholder="e.g. RAM" />
                    <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: '20px', flexShrink: 0 }}>:</span>
                    <input className="admin-input" style={{ ...S.input, flex: 1 }}
                      value={spec.value} onChange={e => updateSpec(i, 'value', e.target.value)} placeholder="e.g. 16GB DDR4" />
                    <button onClick={() => removeSpec(i)} style={S.specRemove} disabled={form.specs.length === 1}>✕</button>
                  </div>
                ))}
              </div>
              {form.specs.some(s => s.key && s.value) && (
                <div style={S.specPreview}>
                  <div style={S.specPreviewTitle}>📋 Specs Preview</div>
                  {form.specs.filter(s => s.key && s.value).map((s, i) => (
                    <div key={i} style={S.specTableRow(i % 2 === 0)}>
                      <span style={S.specKey}>{s.key}</span>
                      <span style={S.specVal}>{s.value}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', paddingTop: '8px' }}>
              {editId && <button onClick={() => { setEditId(null); setForm(emptyForm()); }} style={S.cancelBtn}>Cancel Edit</button>}
              <button onClick={handleSubmit} style={S.submitBtn}>{editId ? '💾 Save Changes' : '➕ Add to Catalogue'}</button>
            </div>
          </div>
        )}

        {/* ══ MANAGE STOCK ══ */}
        {tab === 'manage' && (
          <div style={S.area}>
            <div style={S.filterRow}>
              {[['all','All'],['instock','✅ In Stock'],['outofstock','❌ Out of Stock'],['promo','🔥 Promo']].map(([v,l]) => (
                <button key={v} onClick={() => setFilter(v)} style={S.filterBtn(filter === v)}>{l}</button>
              ))}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {filteredProducts.length === 0 && <div style={S.empty}>No products found.</div>}
              {filteredProducts.map(p => (
                <div key={p.id} style={S.productRow(p.outOfStock)}>
                  <div style={S.productThumb}>
                    {(p.images?.[0] || p.image) ? <img src={p.images?.[0] || p.image} alt={p.name} style={S.thumbImg} /> : <span style={{ fontSize: '28px' }}>📦</span>}
                  </div>
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ fontSize: '15px', fontWeight: '700', color: '#fff' }}>{p.name}</div>
                    {p.brand && <div style={{ fontSize: '12px', color: '#CC0000', fontWeight: '600' }}>{p.brand}</div>}
                    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <span style={S.catBadge}>{p.category}</span>
                      {p.isPromo    && <span style={S.promoBadge}>🔥 PROMO</span>}
                      {p.outOfStock && <span style={S.outBadge}>❌ Out of Stock</span>}
                      {p.images?.length > 1 && <span style={S.imgCountBadge}>📷 {p.images.length} photos</span>}
                      {p.video      && <span style={S.videoBadge}>🎥 Video</span>}
                      {p.specs?.length > 0 && <span style={S.specsBadge}>📋 {p.specs.length} specs</span>}
                      {p.forTags?.map(t => <span key={t} style={S.forBadge}>{t}</span>)}
                    </div>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      {p.isPromo
                        ? <><span style={{ fontSize: '15px', fontWeight: '800', color: '#CC0000' }}>USD {p.promoPrice}</span>
                            <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)', textDecoration: 'line-through' }}>USD {p.price}</span></>
                        : <span style={{ fontSize: '15px', fontWeight: '700', color: '#fff' }}>USD {p.price}</span>}
                      <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.35)' }}>👁 {p.views || 0}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '6px', flexShrink: 0 }}>
                    <button onClick={() => startEdit(p)} style={S.iconBtn} title="Edit">✏️</button>
                    <button onClick={() => toggleOutOfStock(p.id)} style={S.iconBtn} title={p.outOfStock ? 'Mark In Stock' : 'Mark Out of Stock'}>
                      {p.outOfStock ? '✅' : '❌'}
                    </button>
                    <button onClick={() => { if(window.confirm('Remove this product?')) removeProduct(p.id); }} style={S.iconBtn} title="Delete">🗑️</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ══ STATISTICS ══ */}
        {tab === 'stats' && (
          <div style={S.area}>
            <div style={S.statCards}>
              {[
                { label: 'Total Products', value: products.length,               icon: '📦', color: '#CC0000' },
                { label: 'In Stock',       value: inStockProducts.length,        icon: '✅', color: '#16a34a' },
                { label: 'Out of Stock',   value: outOfStockProducts.length,     icon: '❌', color: '#dc2626' },
                { label: 'On Promotion',   value: promoProducts.length,          icon: '🔥', color: '#d97706' },
                { label: 'Total Orders',   value: orders.length,                 icon: '🛒', color: '#7c3aed' },
                { label: 'Total Revenue',  value: `$${totalRevenue.toFixed(0)}`, icon: '💰', color: '#CC0000' },
              ].map(s => (
                <div key={s.label} style={S.statCard(s.color)}>
                  <div style={{ fontSize: '28px' }}>{s.icon}</div>
                  <div style={{ fontSize: '28px', fontWeight: '900', color: s.color }}>{s.value}</div>
                  <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.45)', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{s.label}</div>
                </div>
              ))}
            </div>
            <h3 style={{ fontSize: '15px', fontWeight: '700', color: '#fff', marginTop: '8px' }}>Stock by Category</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {categoryStats.filter(c => c.total > 0).map(c => (
                <div key={c.name} style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <span style={{ fontSize: '13px', color: 'rgba(255,255,255,0.65)', width: '100px', flexShrink: 0 }}>{c.name}</span>
                  <div style={{ flex: 1, height: '8px', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '100px', overflow: 'hidden' }}>
                    <div style={{ width: `${(c.inStock/c.total)*100}%`, height: '100%', backgroundColor: '#CC0000', borderRadius: '100px' }} />
                  </div>
                  <span style={{ fontSize: '12px', color: 'rgba(255,255,255,0.45)', width: '50px', textAlign: 'right' }}>{c.inStock}/{c.total}</span>
                </div>
              ))}
              {categoryStats.every(c => c.total === 0) && <div style={S.empty}>No products yet.</div>}
            </div>
          </div>
        )}

        {/* ══ FOR TAGS — multiple images per product entry ══ */}
        {tab === 'fortags' && (
          <div style={S.area}>
            {forTagMsg && <div style={S.successBanner}>{forTagMsg}</div>}
            <p style={S.hint}>
              Each product entry can have <strong style={{ color: '#fff' }}>multiple photos</strong> (swipeable gallery).
              Add a product, upload 3+ images for it, fill in the title and description.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '28px' }}>
              {FOR_TAGS.map(tag => {
                const items = forTagBanners?.[tag] || [];
                return (
                  <div key={tag} style={S.forTagSection}>
                    <div style={S.forTagSectionHeader}>
                      <span style={S.forTagSectionName}>{tag}</span>
                      <span style={S.forTagCount}>{items.length} product{items.length !== 1 ? 's' : ''}</span>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                      {items.map((item, idx) => {
                        const entryImages = item.images?.length ? item.images : (item.image ? [item.image] : []);
                        const uploadId = `ftg-img-${tag}-${idx}`;
                        const isUploading = forTagUploading === `${tag}-${idx}`;
                        return (
                          <div key={idx} style={{ backgroundColor: '#1a1a1a', borderRadius: 10, padding: 14, border: '1px solid rgba(255,255,255,0.08)', display: 'flex', flexDirection: 'column', gap: 12 }}>

                            {/* Entry header */}
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <span style={{ fontSize: 12, fontWeight: 700, color: 'rgba(255,255,255,0.5)', letterSpacing: '0.06em' }}>PRODUCT {idx + 1} · {entryImages.length} photo{entryImages.length !== 1 ? 's' : ''}</span>
                              <button onClick={() => handleRemoveForTagItem(tag, idx)}
                                style={{ background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.3)', color: '#f87171', fontSize: 12, fontWeight: 700, padding: '4px 10px', borderRadius: 6, cursor: 'pointer' }}>
                                🗑 Remove
                              </button>
                            </div>

                            {/* Image gallery row */}
                            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                              {entryImages.map((img, imgIdx) => (
                                <div key={imgIdx} style={{ position: 'relative', width: 80, height: 80, borderRadius: 8, overflow: 'hidden', border: imgIdx === 0 ? '2px solid #CC0000' : '1px solid rgba(255,255,255,0.15)', flexShrink: 0 }}>
                                  <img src={img} alt={`img ${imgIdx+1}`} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                  {imgIdx === 0 && (
                                    <div style={{ position: 'absolute', top: 2, left: 2, fontSize: 9, fontWeight: 800, background: '#CC0000', color: '#fff', padding: '1px 4px', borderRadius: 3 }}>MAIN</div>
                                  )}
                                  <button onClick={() => handleRemoveEntryImage(tag, idx, imgIdx)}
                                    style={{ position: 'absolute', top: 2, right: 2, width: 18, height: 18, borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.7)', color: '#fff', fontSize: 10, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}>✕</button>
                                </div>
                              ))}

                              {/* Add more images to this entry */}
                              <label htmlFor={uploadId}
                                style={{ width: 80, height: 80, borderRadius: 8, border: '1.5px dashed rgba(204,0,0,0.5)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', fontSize: 20, background: 'rgba(204,0,0,0.04)', flexShrink: 0, gap: 2 }}>
                                {isUploading ? '⏳' : '➕'}
                                <span style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.3)' }}>{isUploading ? 'Loading' : 'Add pics'}</span>
                              </label>
                              <input id={uploadId} type="file" accept="image/*" multiple style={{ display: 'none' }}
                                onChange={e => { handleAddImagesToEntry(tag, idx, e.target.files); e.target.value = ''; }} />
                            </div>

                            {/* Title + Description */}
                            <input className="admin-input" style={S.forTagInput}
                              value={item.title || ''}
                              placeholder="Product title (e.g. HP ProBook 440 G11...)"
                              onChange={e => handleForTagTextChange(tag, idx, 'title', e.target.value)} />
                            <textarea className="admin-input" style={{ ...S.forTagInput, minHeight: 70, resize: 'vertical' }}
                              value={item.description || ''}
                              placeholder="Description / specs..."
                              onChange={e => handleForTagTextChange(tag, idx, 'description', e.target.value)} />
                          </div>
                        );
                      })}

                      {/* Add new product entry */}
                      <button onClick={() => handleAddForTagProduct(tag)}
                        style={{ padding: '12px', borderRadius: 10, border: '1.5px dashed rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.02)', color: 'rgba(255,255,255,0.5)', fontSize: 14, fontWeight: 700, cursor: 'pointer', textAlign: 'center' }}>
                        ➕ Add Product to {tag}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* ══ SECURITY ══ */}
        {tab === 'security' && (
          <div style={S.area}>
            <div style={S.securityCard}>
              <div style={S.securityIcon}>🔐</div>
              <h3 style={S.secTitle}>Change Admin PIN</h3>
              <p style={S.secDesc}>
                Your PIN is stored securely as a SHA-256 hash in Firebase — it is never saved as plain text anywhere.
                Choose something only you know, at least 6 characters.
              </p>

              {pinMsg && (
                <div style={pinMsg.type === 'success' ? S.successBanner : S.errorBanner}>
                  {pinMsg.text}
                </div>
              )}

              <div style={S.pinFields}>
                <div style={S.field}>
                  <label style={S.label}>Current PIN</label>
                  <div className="pin-input-wrap">
                    <input className="admin-input" style={S.input} type={showCurrent ? 'text' : 'password'}
                      placeholder="Enter current PIN" value={currentPin} onChange={e => setCurrentPin(e.target.value)} />
                    <button className="pin-eye" onClick={() => setShowCurrent(v => !v)}>{showCurrent ? '🙈' : '👁'}</button>
                  </div>
                </div>
                <div style={S.field}>
                  <label style={S.label}>New PIN</label>
                  <div className="pin-input-wrap">
                    <input className="admin-input" style={S.input} type={showNew ? 'text' : 'password'}
                      placeholder="At least 6 characters" value={newPin} onChange={e => setNewPin(e.target.value)} />
                    <button className="pin-eye" onClick={() => setShowNew(v => !v)}>{showNew ? '🙈' : '👁'}</button>
                  </div>
                  {newPin && <div style={S.strengthBar}><div style={S.strengthFill(newPin)} /></div>}
                  {newPin && <p style={S.strengthLabel(newPin)}>Strength: {newPin.length < 6 ? 'Too short' : newPin.length < 8 ? 'Weak' : newPin.length < 12 ? 'Good' : 'Strong'}</p>}
                </div>
                <div style={S.field}>
                  <label style={S.label}>Confirm New PIN</label>
                  <div className="pin-input-wrap">
                    <input className="admin-input"
                      style={{ ...S.input, borderColor: confirmPin && confirmPin !== newPin ? 'rgba(220,38,38,0.6)' : confirmPin && confirmPin === newPin ? 'rgba(22,163,74,0.6)' : undefined }}
                      type={showConfirm ? 'text' : 'password'} placeholder="Re-enter new PIN"
                      value={confirmPin} onChange={e => setConfirmPin(e.target.value)} />
                    <button className="pin-eye" onClick={() => setShowConfirm(v => !v)}>{showConfirm ? '🙈' : '👁'}</button>
                  </div>
                  {confirmPin && confirmPin !== newPin && <p style={{ fontSize: '12px', color: '#f87171', marginTop: '4px' }}>PINs do not match</p>}
                  {confirmPin && confirmPin === newPin  && <p style={{ fontSize: '12px', color: '#4ade80', marginTop: '4px' }}>✓ PINs match</p>}
                </div>
              </div>

              <button onClick={handleChangePin} disabled={pinLoading} style={S.changePinBtn(pinLoading)}>
                {pinLoading ? 'Updating...' : '🔒 Update PIN'}
              </button>

              <div style={S.secNote}>
                <span style={{ fontSize: '16px' }}>ℹ️</span>
                <p style={{ margin: 0, fontSize: '12px', color: 'rgba(255,255,255,0.4)', lineHeight: 1.6 }}>
                  The default PIN is <strong style={{ color: 'rgba(255,255,255,0.6)' }}>1234</strong>. Change it now if you haven't already.
                  Once changed, only you will know the new PIN — it cannot be recovered, only reset by a developer.
                </p>
              </div>
            </div>
          </div>
        )}

      </div>

      {quotingFor && (
        <QuotationModal initialCustomer={quotingFor} onClose={() => setQuotingFor(null)} />
      )}
    </div>
  );
};

const strengthColor = (pin) => {
  if (pin.length < 6)  return '#dc2626';
  if (pin.length < 8)  return '#d97706';
  if (pin.length < 12) return '#16a34a';
  return '#CC0000';
};

const S = {
  overlay:   { position: 'fixed', inset: 0, zIndex: 9999, backgroundColor: 'rgba(0,0,0,0.78)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', backdropFilter: 'blur(4px)' },
  panel:     { backgroundColor: '#1a1a1a', borderRadius: '20px', width: '100%', maxWidth: '860px', maxHeight: '92vh', overflowY: 'auto', border: '1px solid rgba(204,0,0,0.3)', boxShadow: '0 24px 80px rgba(0,0,0,0.65)' },
  header:    { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '22px 28px', borderBottom: '1px solid rgba(255,255,255,0.06)' },
  title:     { fontSize: '22px', fontWeight: '800', color: '#fff', margin: 0 },
  subtitle:  { fontSize: '13px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' },
  closeBtn:  { background: 'rgba(255,255,255,0.1)', border: 'none', color: '#fff', width: '36px', height: '36px', borderRadius: '50%', cursor: 'pointer', fontSize: '16px' },
  tabs:      { display: 'flex', gap: '4px', padding: '14px 28px 0', flexWrap: 'wrap' },
  tab: (a)  => ({ position: 'relative', padding: '9px 18px', borderRadius: '8px', border: 'none', background: a ? '#CC0000' : 'rgba(255,255,255,0.06)', color: '#fff', fontWeight: '600', fontSize: '13px', cursor: 'pointer', transition: 'all 0.2s' }),
  tabAlert:  { position: 'absolute', top: '-5px', right: '-5px', backgroundColor: '#fbbf24', color: '#000', borderRadius: '50%', width: '18px', height: '18px', fontSize: '10px', fontWeight: '900', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  area:      { padding: '20px 28px 28px', display: 'flex', flexDirection: 'column', gap: '20px' },
  label:     { fontSize: '12px', fontWeight: '700', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.6)', textTransform: 'uppercase', display: 'block', marginBottom: '6px' },
  hint:      { fontSize: '12px', color: 'rgba(255,255,255,0.35)', margin: '0 0 10px' },
  input:     { backgroundColor: '#242424', border: '1.5px solid rgba(255,255,255,0.1)', borderRadius: '10px', padding: '11px 14px', color: '#fff', fontSize: '14px', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' },
  fields:    { display: 'flex', flexDirection: 'column', gap: '16px' },
  row2:      { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' },
  field:     { display: 'flex', flexDirection: 'column', gap: '4px' },
  toggleBtn: (on) => ({ padding: '10px 18px', borderRadius: '10px', border: 'none', cursor: 'pointer', background: on ? 'linear-gradient(135deg,#CC0000,#990000)' : 'rgba(255,255,255,0.08)', color: '#fff', fontWeight: '700', fontSize: '13px', whiteSpace: 'nowrap' }),
  cancelBtn: { padding: '12px 24px', borderRadius: '10px', border: '1.5px solid rgba(255,255,255,0.15)', background: 'transparent', color: '#fff', fontWeight: '600', cursor: 'pointer', fontSize: '14px' },
  submitBtn: { padding: '12px 28px', borderRadius: '10px', border: 'none', background: 'linear-gradient(135deg,#CC0000,#990000)', color: '#fff', fontWeight: '700', fontSize: '15px', cursor: 'pointer', boxShadow: '0 4px 16px rgba(204,0,0,0.35)' },
  successBanner: { background: 'rgba(22,163,74,0.15)', border: '1px solid rgba(22,163,74,0.4)', color: '#4ade80', padding: '12px 16px', borderRadius: '10px', fontSize: '14px', fontWeight: '600' },
  errorBanner:   { background: 'rgba(220,38,38,0.12)', border: '1px solid rgba(220,38,38,0.3)', color: '#f87171', padding: '12px 16px', borderRadius: '10px', fontSize: '14px', fontWeight: '600' },
  editBanner:    { background: 'rgba(234,179,8,0.12)', border: '1px solid rgba(234,179,8,0.3)', color: '#fbbf24', padding: '12px 16px', borderRadius: '10px', fontSize: '14px' },
  addSpecBtn:  { padding: '8px 16px', borderRadius: '8px', border: '1px solid rgba(204,0,0,0.3)', background: 'rgba(204,0,0,0.1)', color: '#CC0000', fontWeight: '700', fontSize: '13px', cursor: 'pointer', whiteSpace: 'nowrap' },
  specRow:     { display: 'flex', alignItems: 'center', gap: '8px' },
  specRemove:  { background: 'rgba(220,38,38,0.15)', border: '1px solid rgba(220,38,38,0.2)', color: '#f87171', cursor: 'pointer', fontSize: '14px', padding: '8px 10px', borderRadius: '8px', flexShrink: 0 },
  specPreview: { marginTop: '12px', borderRadius: '12px', border: '1px solid rgba(255,255,255,0.08)', overflow: 'hidden' },
  specPreviewTitle: { padding: '10px 14px', fontSize: '12px', fontWeight: '700', color: 'rgba(255,255,255,0.5)', letterSpacing: '0.06em', textTransform: 'uppercase', borderBottom: '1px solid rgba(255,255,255,0.06)', backgroundColor: 'rgba(255,255,255,0.02)' },
  specTableRow: (even) => ({ display: 'flex', padding: '9px 14px', backgroundColor: even ? '#242424' : 'transparent', gap: '12px' }),
  specKey:     { fontSize: '13px', fontWeight: '700', color: 'rgba(255,255,255,0.5)', width: '120px', flexShrink: 0 },
  specVal:     { fontSize: '13px', color: '#fff', fontWeight: '600' },
  revenueRow: { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: '12px' },
  revCard: (c) => ({ backgroundColor: '#242424', borderRadius: '12px', padding: '16px', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', border: `1px solid ${c}30`, textAlign: 'center' }),
  revIcon:      { fontSize: '24px' },
  revValue: (c) => ({ fontSize: '26px', fontWeight: '900', color: c }),
  revLabel:     { fontSize: '11px', color: 'rgba(255,255,255,0.45)', textTransform: 'uppercase', letterSpacing: '0.06em' },
  filterRow:    { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  filterBtn: (a) => ({ padding: '7px 16px', borderRadius: '100px', border: 'none', cursor: 'pointer', background: a ? '#CC0000' : 'rgba(255,255,255,0.08)', color: '#fff', fontWeight: '600', fontSize: '13px' }),
  orderList:    { display: 'flex', flexDirection: 'column', gap: '14px' },
  orderCard: (sc) => ({ backgroundColor: '#242424', borderRadius: '14px', border: `1px solid ${sc.border}`, overflow: 'hidden' }),
  orderHeader:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '14px 16px', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  orderId:      { fontSize: '15px', fontWeight: '800', color: '#fff', fontFamily: 'monospace' },
  orderDate:    { fontSize: '12px', color: 'rgba(255,255,255,0.4)', marginTop: '2px' },
  statusBadge: (sc) => ({ fontSize: '12px', fontWeight: '700', padding: '4px 12px', borderRadius: '100px', backgroundColor: sc.bg, color: sc.text, border: `1px solid ${sc.border}` }),
  orderCustomer:{ padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '6px', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  customerRow:  { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', color: 'rgba(255,255,255,0.8)' },
  customerIcon: { fontSize: '16px', flexShrink: 0 },
  customerName: { fontWeight: '700', color: '#fff' },
  orderItems:   { padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '6px', borderBottom: '1px solid rgba(255,255,255,0.05)' },
  orderItem:    { display: 'flex', justifyContent: 'space-between', fontSize: '13px', color: 'rgba(255,255,255,0.7)' },
  orderTotal:   { display: 'flex', justifyContent: 'space-between', fontSize: '16px', fontWeight: '800', color: '#fff', paddingTop: '8px', borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: '4px' },
  orderActions: { padding: '12px 16px', display: 'flex', flexDirection: 'column', gap: '10px' },
  statusBtns:   { display: 'flex', gap: '8px', flexWrap: 'wrap' },
  actionBtn: (c) => ({ padding: '7px 14px', borderRadius: '8px', border: `1px solid ${c}40`, cursor: 'pointer', backgroundColor: `${c}20`, color: c==='#16a34a'?'#4ade80':c==='#d97706'?'#fbbf24':'#f87171', fontWeight: '700', fontSize: '12px' }),
  waBtns:       { display: 'flex', gap: '8px', flexWrap: 'wrap', alignItems: 'center' },
  waBtn:        { padding: '7px 14px', borderRadius: '8px', border: '1px solid rgba(37,211,102,0.3)', backgroundColor: 'rgba(37,211,102,0.08)', color: '#4ade80', fontWeight: '700', fontSize: '12px', cursor: 'pointer' },
  deleteBtn:    { padding: '7px 10px', borderRadius: '8px', border: '1px solid rgba(220,38,38,0.2)', backgroundColor: 'rgba(220,38,38,0.08)', cursor: 'pointer', fontSize: '14px' },
  productRow: (oos) => ({ display: 'flex', gap: '14px', alignItems: 'center', backgroundColor: oos?'rgba(220,38,38,0.05)':'#242424', borderRadius: '12px', padding: '14px', border: `1px solid ${oos?'rgba(220,38,38,0.2)':'rgba(255,255,255,0.06)'}` }),
  productThumb: { width: '64px', height: '64px', borderRadius: '10px', backgroundColor: 'rgba(204,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, overflow: 'hidden' },
  thumbImg:     { width: '100%', height: '100%', objectFit: 'cover' },
  catBadge:     { fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '100px', backgroundColor: 'rgba(204,0,0,0.2)', color: '#ff6666' },
  promoBadge:   { fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '100px', backgroundColor: 'rgba(217,119,6,0.2)', color: '#fbbf24' },
  outBadge:     { fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '100px', backgroundColor: 'rgba(220,38,38,0.2)', color: '#f87171' },
  imgCountBadge:{ fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '100px', backgroundColor: 'rgba(59,130,246,0.2)', color: '#93c5fd' },
  videoBadge:   { fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '100px', backgroundColor: 'rgba(139,92,246,0.2)', color: '#c4b5fd' },
  specsBadge:   { fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '100px', backgroundColor: 'rgba(20,184,166,0.2)', color: '#5eead4' },
  forBadge:     { fontSize: '11px', fontWeight: '600', padding: '2px 8px', borderRadius: '100px', backgroundColor: 'rgba(204,0,0,0.15)', color: '#ff8888' },
  iconBtn:      { padding: '8px 10px', borderRadius: '8px', border: 'none', background: 'rgba(255,255,255,0.08)', cursor: 'pointer', fontSize: '16px' },
  statCards: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: '12px' },
  statCard: (c) => ({ backgroundColor: '#242424', borderRadius: '14px', padding: '20px 16px', textAlign: 'center', border: `1px solid ${c}28`, display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center' }),
  empty:     { textAlign: 'center', color: 'rgba(255,255,255,0.3)', padding: '40px', fontSize: '14px', lineHeight: 1.6 },
  forTagSection:       { backgroundColor: '#242424', borderRadius: '14px', padding: '16px', border: '1px solid rgba(255,255,255,0.06)' },
  forTagSectionHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' },
  forTagSectionName:   { fontSize: '14px', fontWeight: '800', color: '#fff', letterSpacing: '0.06em', textTransform: 'uppercase' },
  forTagCount:         { fontSize: '12px', fontWeight: '600', color: 'rgba(255,255,255,0.4)' },
  forTagItemGrid:      { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(180px,1fr))', gap: '14px' },
  forTagItemCard:      { display: 'flex', flexDirection: 'column', gap: '8px', backgroundColor: '#1e1e1e', borderRadius: '10px', padding: '10px', border: '1px solid rgba(255,255,255,0.05)' },
  forTagThumbWrap:     { position: 'relative', width: '100%', aspectRatio: '4/3', borderRadius: '8px', overflow: 'hidden', backgroundColor: 'rgba(204,0,0,0.08)' },
  forTagImg:           { width: '100%', height: '100%', objectFit: 'cover', display: 'block' },
  forTagThumbRemove:   { position: 'absolute', top: '4px', right: '4px', width: '22px', height: '22px', borderRadius: '50%', border: 'none', background: 'rgba(0,0,0,0.6)', color: '#fff', fontSize: '12px', fontWeight: '700', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 },
  forTagInput:         { backgroundColor: '#242424', border: '1.5px solid rgba(255,255,255,0.1)', borderRadius: '8px', padding: '8px 10px', color: '#fff', fontSize: '12px', fontFamily: 'inherit', outline: 'none', width: '100%', boxSizing: 'border-box' },
  forTagAddCard:       { aspectRatio: '4/3', borderRadius: '8px', border: '1.5px dashed rgba(204,0,0,0.4)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'rgba(255,255,255,0.5)', fontSize: '24px', backgroundColor: 'rgba(204,0,0,0.04)', alignSelf: 'flex-start', width: '100%' },
  forTagBtn: (active) => ({ padding: '8px 16px', borderRadius: '100px', border: 'none', cursor: 'pointer', background: active ? 'linear-gradient(135deg,#CC0000,#990000)' : 'rgba(255,255,255,0.08)', color: '#fff', fontWeight: '700', fontSize: '12px' }),
  securityCard:   { backgroundColor: '#242424', borderRadius: '16px', padding: '28px', display: 'flex', flexDirection: 'column', gap: '20px', border: '1px solid rgba(204,0,0,0.2)' },
  securityIcon:   { fontSize: '40px', textAlign: 'center' },
  secTitle:       { fontSize: '20px', fontWeight: '800', color: '#fff', margin: 0, textAlign: 'center' },
  secDesc:        { fontSize: '13px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.7, textAlign: 'center', margin: 0 },
  pinFields:      { display: 'flex', flexDirection: 'column', gap: '16px' },
  strengthBar:    { height: '4px', backgroundColor: 'rgba(255,255,255,0.08)', borderRadius: '100px', marginTop: '8px', overflow: 'hidden' },
  strengthFill:   (pin) => ({ height: '100%', borderRadius: '100px', backgroundColor: strengthColor(pin), width: pin.length < 6 ? '20%' : pin.length < 8 ? '40%' : pin.length < 12 ? '70%' : '100%', transition: 'width 0.3s, background-color 0.3s' }),
  strengthLabel:  (pin) => ({ fontSize: '11px', color: strengthColor(pin), marginTop: '4px', fontWeight: '600' }),
  changePinBtn:   (loading) => ({ padding: '13px', borderRadius: '10px', border: 'none', background: loading ? 'rgba(204,0,0,0.4)' : 'linear-gradient(135deg,#CC0000,#990000)', color: '#fff', fontWeight: '700', fontSize: '15px', cursor: loading ? 'not-allowed' : 'pointer', boxShadow: '0 4px 16px rgba(204,0,0,0.35)', width: '100%' }),
  secNote:        { display: 'flex', gap: '10px', alignItems: 'flex-start', backgroundColor: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '10px', padding: '12px 14px' },
};

export default AdminPanel;
